import React, { useState } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger,
} from '../ui/dialog';
import { KeyRound } from 'lucide-react';
import { toast } from '../../hooks/use-toast';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ChangePasswordDialog = () => {
  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [saving, setSaving] = useState(false);

  const reset = () => { setCurrent(''); setNext(''); setConfirm(''); };

  const submit = async () => {
    if (next.length < 8) {
      toast({ title: 'New password must be at least 8 characters', variant: 'destructive' });
      return;
    }
    if (next !== confirm) {
      toast({ title: 'New passwords do not match', variant: 'destructive' });
      return;
    }
    setSaving(true);
    try {
      await axios.post(
        `${API}/admin/auth/change-password`,
        { currentPassword: current, newPassword: next },
        { headers: { Authorization: `Bearer ${sessionStorage.getItem('adminToken')}` } },
      );
      toast({ title: 'Password changed successfully' });
      reset();
      setOpen(false);
    } catch (err) {
      toast({
        title: 'Could not change password',
        description: err.response?.data?.detail || 'Try again',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) reset(); }}>
      <DialogTrigger asChild>
        <Button variant="outline" data-testid="admin-change-password-btn">
          <KeyRound className="w-4 h-4 mr-2" />
          Change Password
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md" data-testid="change-password-dialog">
        <DialogHeader>
          <DialogTitle>Change admin password</DialogTitle>
          <DialogDescription>
            Enter your current password, then choose a new one (at least 8 characters).
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label className="text-xs text-gray-600 mb-1 block">Current password</Label>
            <Input type="password" value={current} onChange={(e) => setCurrent(e.target.value)} data-testid="cp-current" />
          </div>
          <div>
            <Label className="text-xs text-gray-600 mb-1 block">New password</Label>
            <Input type="password" value={next} onChange={(e) => setNext(e.target.value)} data-testid="cp-new" />
          </div>
          <div>
            <Label className="text-xs text-gray-600 mb-1 block">Confirm new password</Label>
            <Input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} data-testid="cp-confirm" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid="cp-submit">
            {saving ? 'Saving…' : 'Update password'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ChangePasswordDialog;
